/**
 * Solitaire Game — Main Container Component
 *
 * Orchestrates: draw mode selection → dealing → card interaction →
 * movement → validation → completion.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Click-based interaction: select card, then click target.
 * Uses refs for state in callbacks to avoid stale closures.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { SolitaireEngine } from '../SolitaireEngine';
import { SolitaireBoard } from './SolitaireBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { SolitaireErrorHandler } from '../services/SolitaireErrorHandler';
import { SolitaireStatisticsService } from '../services/SolitaireStatisticsService';
import { SolitaireGamePersistence } from '../services/SolitaireGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  Card,
  CardSource,
  SolitaireGameSettings,
  SolitaireGameState,
  SolitaireGameProps,
} from '../types';
import './SolitaireGame.css';

type GameView = 'mode-select' | 'playing' | 'completed';

const SolitaireGameInner: React.FC<SolitaireGameProps> = ({ onExit }) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<SolitaireGameSettings | null>(null);
  const [gameState, setGameState] = useState<SolitaireGameState | null>(null);
  const [selectedCard, setSelectedCard] = useState<{ source: CardSource; card: Card } | null>(null);
  const [timer, setTimer] = useState(0);

  // Refs to avoid stale closures
  const engineRef = useRef<SolitaireEngine>(new SolitaireEngine());
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const settingsRef = useRef<SolitaireGameSettings | null>(null);
  const timerValueRef = useRef(0);
  const viewRef = useRef<GameView>('mode-select');

  // Services (lazy init)
  const errorHandlerRef = useRef<SolitaireErrorHandler | null>(null);
  const statsServiceRef = useRef<SolitaireStatisticsService | null>(null);
  const persistenceRef = useRef<SolitaireGamePersistence | null>(null);

  // Keep refs in sync
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    timerValueRef.current = timer;
  }, [timer]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new SolitaireErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new SolitaireStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new SolitaireGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Solitaire services:', e);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer management
  useEffect(() => {
    if (view === 'playing' && gameState?.gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          const newTime = prev + 1;
          timerValueRef.current = newTime;
          engineRef.current.updateTimer(newTime);
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [view, gameState?.gameStatus]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: SolitaireGameSettings) => {
      const engine = engineRef.current;
      const state = engine.initializeGame(newSettings.drawMode);

      setSettings(newSettings);
      settingsRef.current = newSettings;
      setGameState(state);
      setSelectedCard(null);
      setTimer(0);
      timerValueRef.current = 0;
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('solitaire');

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Cards Dealt',
        message: `Draw ${newSettings.drawMode} mode. Good luck!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const autoSaveState = useCallback((state: SolitaireGameState) => {
    if (persistenceRef.current && settingsRef.current) {
      persistenceRef.current.autoSave(state, {
        drawMode: settingsRef.current.drawMode,
        startedAt: state.startedAt,
        lastSavedAt: Date.now(),
      });
    }
  }, []);

  const handleGameComplete = useCallback(
    (state: SolitaireGameState) => {
      setView('completed');
      viewRef.current = 'completed';

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settingsRef.current) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            drawMode: settingsRef.current.drawMode,
            completed: true,
            time: timerValueRef.current,
            moveCount: state.moveCount,
            completedAt: Date.now(),
          },
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 You Won!',
        message: `Completed in ${formatTime(timerValueRef.current)} with ${state.moveCount} moves!`,
        duration: 5000,
      });
    },
    [addNotification, isAuthenticated, user]
  );

  // =========================================================================
  // Card Interaction
  // =========================================================================

  const handleStockClick = useCallback(() => {
    if (!gameState || gameState.gameStatus !== 'playing') return;

    setSelectedCard(null);

    try {
      const engine = engineRef.current;
      const newState = engine.drawFromStock();
      setGameState(newState);
      autoSaveState(newState);
    } catch (error) {
      console.warn('Draw error:', error);
    }
  }, [gameState, autoSaveState]);

  const handleCardClick = useCallback(
    (source: CardSource, card: Card) => {
      if (!gameState || gameState.gameStatus !== 'playing') return;

      // Click face-down tableau card to flip — handled by engine when it's the top card
      if (!card.faceUp && source.type === 'tableau') {
        // Only flip if it's the top card of the column
        const column = gameState.tableau[source.index];
        if (column.length > 0 && column[column.length - 1].id === card.id) {
          // The card should already be face-up if it's the top card after dealing.
          // If it's face-down, it means there's a card above it that was moved.
          // Actually, face-down top cards get flipped by the engine automatically.
          // This case shouldn't normally happen, but just deselect.
          setSelectedCard(null);
        }
        return;
      }

      // If clicking the already-selected card, deselect
      if (selectedCard && selectedCard.card.id === card.id) {
        setSelectedCard(null);
        return;
      }

      // If a card is already selected, try to move it to this location
      if (selectedCard) {
        const targetSource = source;

        try {
          const engine = engineRef.current;
          let newState: SolitaireGameState;

          if (targetSource.type === 'foundation') {
            // Move selected card to foundation
            newState = engine.moveToFoundation(selectedCard.source);
          } else if (targetSource.type === 'tableau') {
            // Move selected card/sequence to this tableau column
            newState = engine.moveToTableau(selectedCard.source, targetSource.index);
          } else {
            // Can't move to stock or waste
            setSelectedCard({ source, card });
            return;
          }

          setSelectedCard(null);
          setGameState(newState);
          autoSaveState(newState);

          if (newState.gameStatus === 'completed') {
            handleGameComplete(newState);
          }
          return;
        } catch {
          // Invalid move — select the new card instead
        }
      }

      // Select this card
      setSelectedCard({ source, card });
    },
    [gameState, selectedCard, autoSaveState, handleGameComplete]
  );

  const handleEmptyClick = useCallback(
    (source: CardSource) => {
      if (!gameState || gameState.gameStatus !== 'playing') return;
      if (!selectedCard) return;

      try {
        const engine = engineRef.current;
        let newState: SolitaireGameState;

        if (source.type === 'foundation') {
          newState = engine.moveToFoundation(selectedCard.source);
        } else if (source.type === 'tableau') {
          newState = engine.moveToTableau(selectedCard.source, source.index);
        } else {
          return;
        }

        setSelectedCard(null);
        setGameState(newState);
        autoSaveState(newState);

        if (newState.gameStatus === 'completed') {
          handleGameComplete(newState);
        }
      } catch {
        // Invalid move — just deselect
        setSelectedCard(null);
      }
    },
    [gameState, selectedCard, autoSaveState, handleGameComplete]
  );

  const handleCardDoubleClick = useCallback(
    (source: CardSource, card: Card) => {
      if (!gameState || gameState.gameStatus !== 'playing') return;
      if (!card.faceUp) return;

      // Only auto-move the top card of a pile
      const isTopCard = (() => {
        if (source.type === 'waste') {
          return gameState.waste.length > 0 && gameState.waste[gameState.waste.length - 1].id === card.id;
        }
        if (source.type === 'tableau') {
          const col = gameState.tableau[source.index];
          return col.length > 0 && col[col.length - 1].id === card.id;
        }
        return false;
      })();

      if (!isTopCard) return;

      try {
        const engine = engineRef.current;
        const newState = engine.moveToFoundation(source);
        setSelectedCard(null);
        setGameState(newState);
        autoSaveState(newState);

        if (newState.gameStatus === 'completed') {
          handleGameComplete(newState);
        }
      } catch {
        // Can't move to foundation — ignore
      }
    },
    [gameState, autoSaveState, handleGameComplete]
  );

  // =========================================================================
  // Actions
  // =========================================================================

  const handleUndo = useCallback(() => {
    if (!gameState || gameState.gameStatus !== 'playing') return;

    try {
      const engine = engineRef.current;
      const newState = engine.undo();
      setSelectedCard(null);
      setGameState(newState);
      autoSaveState(newState);
    } catch (error) {
      console.warn('Undo error:', error);
    }
  }, [gameState, autoSaveState]);

  const handleAutoComplete = useCallback(() => {
    if (!gameState || gameState.gameStatus !== 'playing') return;

    const engine = engineRef.current;
    if (!engine.canAutoComplete()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Cannot Auto-Complete',
        message: 'All cards must be face-up first.',
        duration: 3000,
      });
      return;
    }

    try {
      const newState = engine.autoComplete();
      setSelectedCard(null);
      setGameState(newState);

      if (newState.gameStatus === 'completed') {
        handleGameComplete(newState);
      }
    } catch (error) {
      console.warn('Auto-complete error:', error);
    }
  }, [gameState, addNotification, handleGameComplete]);

  const handleNewGame = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setView('mode-select');
    viewRef.current = 'mode-select';
    setGameState(null);
    setSettings(null);
    settingsRef.current = null;
    setSelectedCard(null);
    setTimer(0);
    timerValueRef.current = 0;
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Keyboard support
  // =========================================================================

  useEffect(() => {
    if (view !== 'playing' || !gameState || gameState.gameStatus !== 'playing') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to deselect
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedCard(null);
        return;
      }

      // Z for undo (Ctrl+Z or Cmd+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
        return;
      }

      // Space or Enter to draw from stock
      if (e.key === ' ' || e.key === 'Enter') {
        if (!selectedCard) {
          e.preventDefault();
          handleStockClick();
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, gameState, selectedCard, handleUndo, handleStockClick]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const solitaireConfig: GameModeConfig = {
      gameName: 'Solitaire',
      gameIcon: '🃏',
      availableModes: ['ai'],
      difficulties: [],
      extraSections: [{
        title: 'Draw Mode',
        icon: '🂠',
        options: [
          { id: '1', label: 'Draw 1', icon: '1️⃣', description: 'Easier — draw one card at a time' },
          { id: '3', label: 'Draw 3', icon: '3️⃣', description: 'Classic — draw three cards at a time' },
        ],
        defaultSelected: '1',
        defaultOpen: true,
        visibleForModes: ['ai'],
      }],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      const drawMode = parseInt(selection.extras?.['extra-0'] || '1', 10) as 1 | 3;
      handleStartGame({ drawMode });
    };

    return (
      <div className="solitaire-game">
        <div className="solitaire-game__header">
          <button
            className="solitaire-game__exit-btn"
            onClick={handleExit}
            aria-label="Back to Game Hub"
          >
            ← Game Hub
          </button>
        </div>
        <UniversalGameModeSelector
          config={solitaireConfig}
          onStart={handleModeSelection}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const canAutoCompleteNow = engineRef.current.canAutoComplete();

  const gameOverConfig = view === 'completed' ? {
    show: true,
    result: 'win' as const,
    title: '🎉 You Won!',
    subtitle: `Completed in ${formatTime(timer)} with ${gameState.moveCount} moves`,
    icon: '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Solitaire',
    board: (
      <div className="solitaire-game__board-area">
        <SolitaireBoard
          gameState={gameState}
          selectedCard={selectedCard}
          onCardClick={handleCardClick}
          onEmptyClick={handleEmptyClick}
          onCardDoubleClick={handleCardDoubleClick}
          onStockClick={handleStockClick}
          disabled={gameState.gameStatus !== 'playing'}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'info',
        title: 'Game Info',
        icon: '📋',
        content: (
          <div className="solitaire-game__info-bar" role="status" aria-live="polite">
            <div className="solitaire-game__info-item">
              <span className="solitaire-game__info-label">Mode</span>
              <span className="solitaire-game__info-value">Draw {settings?.drawMode}</span>
            </div>
            <div className="solitaire-game__info-item">
              <span className="solitaire-game__info-label">Time</span>
              <span className="solitaire-game__info-value solitaire-game__timer">
                {formatTime(timer)}
              </span>
            </div>
            <div className="solitaire-game__info-item">
              <span className="solitaire-game__info-label">Moves</span>
              <span className="solitaire-game__info-value">{gameState.moveCount}</span>
            </div>
          </div>
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          {gameState.gameStatus === 'completed'
            ? 'Game Complete!'
            : `Draw ${settings?.drawMode} — ${formatTime(timer)}`}
        </span>
      ),
      gameStatus: gameState.gameStatus === 'completed' ? 'Game Complete' : undefined,
      gameStatusType: gameState.gameStatus === 'completed' ? 'ended' : 'active',
    },
    actions: [
      { id: 'undo', label: 'Undo', icon: '↩', onClick: handleUndo, variant: 'secondary', disabled: gameState.moveHistory.length === 0 },
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      ...(canAutoCompleteNow ? [{ id: 'auto-complete', label: 'Auto-Complete', icon: '✨', onClick: handleAutoComplete, variant: 'accent' as const }] : []),
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="solitaire-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Format seconds as MM:SS */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Wrapped in ErrorBoundary */
const SolitaireGame: React.FC<SolitaireGameProps> = (props) => (
  <ErrorBoundary componentName="Solitaire">
    <SolitaireGameInner {...props} />
  </ErrorBoundary>
);

export { SolitaireGame };
export default SolitaireGame;
